import {
  DiscountType,
  EventDetails,
  EventType,
  FormInputType,
} from '@graphql/__generated/graphql'
import { EventCreation } from '@libs/CreateEventContext'
import { isEqual } from 'lodash'
import moment from 'moment'

export type JsonPatchOperation = {
  op: 'test' | 'add' | 'replace' | 'remove'
  path: string
  value?: any
}

export const cloneJson = <T,>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value))

const isPlainObject = (value: any) =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

export const canonicalizePatchValue = (value: any): any => {
  if (Array.isArray(value)) return value.map(canonicalizePatchValue)
  if (value === null || value === undefined) return undefined
  if (!isPlainObject(value)) return value

  return Object.entries(value).reduce<Record<string, any>>(
    (payload, [key, child]) => {
      if (key === '__typename') return payload
      const canonicalChild = canonicalizePatchValue(child)
      if (canonicalChild !== undefined) payload[key] = canonicalChild
      return payload
    },
    {}
  )
}

const hasConfiguredRegistrationOptions = (field: any) =>
  Array.isArray(field?.options) &&
  field.options.some((option: any) => {
    if (typeof option === 'string') return option.trim() !== ''
    return [option?.label, option?.value].some(
      (value) => typeof value === 'string' && value.trim() !== ''
    )
  })

const normalizeRegistrationFields = (fields: any[] | null | undefined) =>
  (fields ?? []).map((field) => ({
    ...field,
    type:
      field?.type ??
      (hasConfiguredRegistrationOptions(field)
        ? FormInputType.Dropdown
        : FormInputType.Text),
  }))

const normalizeDiscounts = (discounts: any[] | null | undefined) =>
  (discounts ?? []).map((discount) => {
    const type = discount?.type ?? DiscountType.Percentage

    return {
      ...discount,
      type,
      tickets: discount?.tickets ?? [],
      ...(type === DiscountType.Percentage
        ? { percentage: discount?.percentage ?? 0 }
        : {}),
    }
  })

const escapeJsonPointerSegment = (segment: string) =>
  segment.replace(/~/g, '~0').replace(/\//g, '~1')

const toJsonPointer = (segments: string[]) =>
  `/${segments.map(escapeJsonPointerSegment).join('/')}`

const parseJsonPointer = (path: string): string[] => {
  if (!path || path[0] !== '/') {
    throw new Error(`Invalid JSON pointer path: ${path}`)
  }

  return path
    .slice(1)
    .split('/')
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'))
}

const getPointerParent = (target: any, path: string) => {
  const segments = parseJsonPointer(path)
  const key = segments.pop()

  if (!key) throw new Error(`Invalid JSON pointer path: ${path}`)

  const parent = segments.reduce((current, segment) => {
    if (current === undefined || current === null) return undefined
    return current[segment]
  }, target)

  return { parent, key }
}

const setPointerValue = (target: any, path: string, value: any) => {
  const { parent, key } = getPointerParent(target, path)
  if (parent === undefined || parent === null) {
    throw new Error(`Cannot set missing JSON pointer path: ${path}`)
  }
  parent[key] = value
}

const removePointerValue = (target: any, path: string) => {
  const { parent, key } = getPointerParent(target, path)
  if (parent === undefined || parent === null) {
    throw new Error(`Cannot remove missing JSON pointer path: ${path}`)
  }
  delete parent[key]
}

export const applyPatchOperationsToDraft = (
  latestEvent: Partial<EventCreation>,
  operations: JsonPatchOperation[]
) => {
  const rebasedDraft = cloneJson(latestEvent)

  operations
    .filter((operation) => operation.op !== 'test')
    .forEach((operation) => {
      if (operation.op === 'remove') {
        removePointerValue(rebasedDraft, operation.path)
      } else {
        setPointerValue(rebasedDraft, operation.path, cloneJson(operation.value))
      }
    })

  return rebasedDraft
}

export const buildPatchOperations = (
  baseValue: any,
  draftValue: any,
  path: string[] = []
): JsonPatchOperation[] => {
  const cleanBase = canonicalizePatchValue(baseValue)
  const cleanDraft = canonicalizePatchValue(draftValue)

  if (isEqual(cleanBase, cleanDraft)) return []

  if (
    path.length > 0 &&
    isPlainObject(cleanBase) &&
    isPlainObject(cleanDraft)
  ) {
    return Array.from(
      new Set([...Object.keys(cleanBase), ...Object.keys(cleanDraft)])
    ).flatMap((key) =>
      buildPatchOperations(cleanBase[key], cleanDraft[key], [...path, key])
    )
  }

  if (path.length === 0) {
    return Array.from(
      new Set([
        ...Object.keys(cleanBase ?? {}),
        ...Object.keys(cleanDraft ?? {}),
      ])
    ).flatMap((key) =>
      buildPatchOperations(cleanBase?.[key], cleanDraft?.[key], [key])
    )
  }

  const pointer = toJsonPointer(path)
  const operation: JsonPatchOperation = {
    op:
      cleanDraft === undefined
        ? 'remove'
        : cleanBase === undefined
          ? 'add'
          : 'replace',
    path: pointer,
  }
  if (cleanDraft !== undefined) operation.value = cloneJson(cleanDraft)

  return [
    { op: 'test', path: pointer, value: cloneJson(cleanBase) },
    operation,
  ]
}

export const buildEditableEventPayload = (
  input: Partial<EventCreation> | undefined,
  profile?: {
    university?: string | null
    company?: string | null
    fullName?: string | null
  },
  fundraiserDefaults = {
    startDateTime: moment().toISOString(),
    registrationCloseDateTime: moment().add(14, 'd').toISOString(),
    endDateTime: moment().add(1, 'M').toISOString(),
  }
) => {
  let event = { ...(input?.event ?? {}) } as EventDetails
  let { organizationName } = event

  if (
    !organizationName ||
    organizationName === 'Unknown' ||
    organizationName === 'Default Event Organization'
  ) {
    organizationName =
      profile?.university ??
      profile?.company ??
      profile?.fullName ??
      'Default Event Organization'
  }
  if (organizationName) event.organizationName = organizationName

  if (event?.eventType === EventType.Fundraiser) {
    event = {
      ...event,
      startDateTime: event?.startDateTime ?? fundraiserDefaults.startDateTime,
      registrationCloseDateTime:
        event?.registrationCloseDateTime ??
        fundraiserDefaults.registrationCloseDateTime,
      endDateTime: event?.endDateTime ?? fundraiserDefaults.endDateTime,
    } as EventDetails
  }

  event = { ...event, publiclyListed: event?.publiclyListed ?? true }

  return canonicalizePatchValue({
    isPublished: input?.isPublished,
    isPremium: input?.isPremium,
    event,
    discounts: normalizeDiscounts(input?.discounts),
    attendees: input?.attendees ?? [],
    tickets: input?.tickets ?? [],
    registrationFields: normalizeRegistrationFields(input?.registrationFields),
    speakers: input?.speakers ?? [],
    stages: input?.stages ?? [],
    sessions: input?.sessions ?? [],
    sponsors: input?.sponsors ?? [],
    ondemandContent: input?.ondemandContent ?? [],
    communications: input?.communications ?? {},
    notifications: input?.notifications ?? [],
    emailTemplates: input?.emailTemplates ?? [],
    breakoutRooms: input?.breakoutRooms ?? [],
    fundraising: { ...(input?.fundraising ?? {}) },
    notifyOrganiser: input?.notifyOrganiser ?? false,
  })
}

const getAttendeeIdentity = (attendee: any) => {
  const normalize = (value: any) =>
    typeof value === 'string' ? value.trim().toLowerCase() : undefined
  const ticketCode = normalize(attendee?.ticketCode)
  const orderId = normalize(attendee?.orderId)
  const email = normalize(attendee?.email)
  const ticketTitle = normalize(attendee?.ticketTitle)
  const userId = normalize(attendee?.userId)

  if (ticketCode) return `ticket:${ticketCode}`
  if (orderId && email && ticketTitle)
    return `order:${orderId}:${email}:${ticketTitle}`
  if (userId && email) return `user:${userId}:${email}`
  if (email && ticketTitle) return `manual:${email}:${ticketTitle}`
  return undefined
}

type AttendeeMapResult =
  | { success: true; attendees: Map<string, any> }
  | { success: false; message: string }

const buildAttendeeMap = (attendees: any[] = []): AttendeeMapResult => {
  const result = new Map<string, any>()
  for (const attendee of attendees) {
    const identity = getAttendeeIdentity(attendee)
    if (!identity) {
      return {
        success: false,
        message: 'An attendee has no stable identity and cannot be reapplied.',
      }
    }
    if (result.has(identity)) {
      return {
        success: false,
        message: 'Duplicate attendees cannot be reapplied automatically.',
      }
    }
    result.set(identity, canonicalizePatchValue(attendee))
  }
  return { success: true, attendees: result }
}

export type AttendeeRebaseResult =
  | { success: true; attendees: any[]; removedCount: number; changedCount: number }
  | { success: false; message: string }

export const rebaseAttendeeChanges = ({
  baseAttendees = [],
  draftAttendees = [],
  latestAttendees = [],
}: {
  baseAttendees?: any[]
  draftAttendees?: any[]
  latestAttendees?: any[]
}): AttendeeRebaseResult => {
  const cleanBase = canonicalizePatchValue(baseAttendees) ?? []
  const cleanDraft = canonicalizePatchValue(draftAttendees) ?? []
  const cleanLatest = canonicalizePatchValue(latestAttendees) ?? []
  const baseResult = buildAttendeeMap(cleanBase)
  const draftResult = buildAttendeeMap(cleanDraft)
  const latestResult = buildAttendeeMap(cleanLatest)
  if (!baseResult.success) return baseResult
  if (!draftResult.success) return draftResult
  if (!latestResult.success) return latestResult

  const baseMap = baseResult.attendees
  const draftMap = draftResult.attendees
  const latestMap = latestResult.attendees
  const removedKeys = Array.from(baseMap.keys()).filter(
    (key) => !draftMap.has(key)
  )
  const changedEntries = Array.from(draftMap.entries()).filter(
    ([key, attendee]) => !isEqual(baseMap.get(key), attendee)
  )

  if (removedKeys.length === 0 && changedEntries.length === 0) {
    return { success: false, message: 'No attendee changes were available to reapply.' }
  }

  for (const key of removedKeys) {
    if (!latestMap.has(key)) continue
    if (!isEqual(latestMap.get(key), baseMap.get(key))) {
      return { success: false, message: 'An attendee you removed changed elsewhere.' }
    }
    latestMap.delete(key)
  }

  for (const [key, draftAttendee] of changedEntries) {
    const baseAttendee = baseMap.get(key)
    const latestAttendee = latestMap.get(key)
    if (!baseAttendee) {
      if (latestAttendee && !isEqual(latestAttendee, draftAttendee)) {
        return {
          success: false,
          message: 'An attendee you added already exists with different data.',
        }
      }
      latestMap.set(key, draftAttendee)
      continue
    }
    if (!latestAttendee) {
      return { success: false, message: 'An attendee you edited was removed elsewhere.' }
    }
    if (!isEqual(latestAttendee, baseAttendee)) {
      return { success: false, message: 'An attendee you edited changed elsewhere.' }
    }
    latestMap.set(key, draftAttendee)
  }

  return {
    success: true,
    attendees: Array.from(latestMap.values()),
    removedCount: removedKeys.length,
    changedCount: changedEntries.length,
  }
}
